import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { BoxProps, Button } from 'rebass'
import { s18n } from '../i18n'
import { sTextHoverColor } from '../styles/helpers'
import { SStyled, useSTheme } from 'styles/theme'
import SText from './opinionless/SText'
import { useUpdateLanguageMutation } from 'types/graphql'
import { UserContext } from 'v2/context/user'

import * as moment from 'moment'
import 'moment/locale/es'
import ConditionalRender from '../utils/conditionalRender'
import { HoverBox, LanguageSwitcherContainer, RelativeContainer } from './styles'

import { FormControl, Icon, NativeSelect } from '@material-ui/core'
import SBox from './opinionless/SBox'
import SFlex from './opinionless/SFlex'
import { usePotentialUser } from '../v2/context/potentialUser'

interface Props extends BoxProps {
    isAuthed: boolean
    switcherId: string
    defaultLanguage: string
}

export const LanguageSwitcherStyles = SStyled(SFlex)`    
    .MuiNativeSelect-icon {
        color: white !important;
    }`

const LanguageSwitcher: React.FC<Props> = (props: Props) => {
    const { isAuthed, switcherId, ...boxProps } = props
    const { authenticatedUser } = useContext(UserContext)
    const { potentialUser } = usePotentialUser()

    const [languages, setLanguages] = useState(window.LANGUAGES)
    const [updateLanguage, { data, loading, error }] = useUpdateLanguageMutation()
    const theme = useSTheme()
    const isLongBeach = useMemo(() => {
        return window.PROGRAM.name.toLowerCase().includes('long') && window.PROGRAM.name.toLowerCase().includes('beach')
    }, [window])

    moment.locale(s18n.language)

    const switchToLanguage = useCallback(
        (language: string) => {
            s18n.changeLanguage(language)
            moment.locale(language)
            if (authenticatedUser) {
                updateLanguage({
                    variables: {
                        language,
                    },
                })
                // authenticatedUser.language = language.toUpperCase()
            }
        },
        [authenticatedUser]
    )

    useEffect(() => {
        
        switchToLanguage(
            authenticatedUser?.language.toLowerCase() ||
                potentialUser?.language.toLowerCase() ||
                props.defaultLanguage ||
                s18n.language
        )
    }, [props.defaultLanguage])

    const [target, setTarget] = useState(null)

    useEffect(() => {
        const target = document.getElementById(`${switcherId}-languageSwitcherButton-${s18n.language}`)
        setTarget(target)

        moment.locale(s18n.language)
    }, [s18n, s18n.language])

    return (
        <React.Fragment>
            <ConditionalRender if={languages?.length > 1}>
                <LanguageSwitcherContainer
                    isAuthed={isAuthed}
                    {...boxProps}
                    sx={{ marginTop: isLongBeach ? '-3px' : 0 }}
                >
                    <RelativeContainer
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'stretch',
                        }}
                    >
                        <ConditionalRender if={switcherId !== 'mobile' || !isLongBeach}>
                            {languages?.map((language) => (
                                <React.Fragment key={language.code}>
                                    <LanguageSwitcherButton
                                        switcherId={switcherId}
                                        language={language}
                                        isCurrentLanguage={language.code === s18n.language}
                                        onClick={switchToLanguage}
                                        isAuthed={isAuthed}
                                    />
                                </React.Fragment>
                            ))}
                        </ConditionalRender>

                        <ConditionalRender if={switcherId === 'mobile' && isLongBeach}>
                            <LanguageSwitcherStyles>
                                <MobileLanguageSwitcherDropDown
                                    switcherId={switcherId}
                                    languages={languages}
                                    isCurrentLanguage={true}
                                    onClick={switchToLanguage}
                                    isAuthed={isAuthed}
                                />
                            </LanguageSwitcherStyles>
                        </ConditionalRender>

                        {target && <HoverBox left={target.offsetLeft} width={target.offsetWidth} />}
                    </RelativeContainer>
                </LanguageSwitcherContainer>
            </ConditionalRender>
        </React.Fragment>
    )
}

interface LanguageSwitcherButtonProps {
    language: {
        code: string
        text: string
    }
    isCurrentLanguage: boolean
    onClick: (language: string) => void
    isAuthed: boolean
    switcherId: string
}

const LanguageSwitcherButton = (props: LanguageSwitcherButtonProps) => {
    const { language, isCurrentLanguage, switcherId, onClick: onClickProp, isAuthed } = props
    const theme = useSTheme()
    const onClick = useCallback(() => {
        onClickProp(language.code)
    }, [language, onClickProp])

    let color = isCurrentLanguage ? 'white' : theme.colors.languageSwitcherLBDisabled || theme.colors.tertiaryColor
    if (isAuthed) {
        color = isCurrentLanguage ? 'white' : theme.colors.languageSwitcherLBDisabled || theme.colors.secondaryColor
    }

    return (
        <Button
            backgroundColor={
                isCurrentLanguage ? theme.colors.secondaryColor : theme.colors.languageSwitcherInactiveColor
            }
            className="notranslate"
            id={`${switcherId}-languageSwitcherButton-${language.code}`}
            sx={{
                color: isCurrentLanguage ? 'black' : 'gray',
                fontFamily: theme.fonts.longBeach.body,
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                ...(!isCurrentLanguage ? sTextHoverColor('lightGray') : {}),
            }}
            pl={4}
            pr={4}
            pt={4}
            pb={4}
            onClick={onClick}
        >
            <SText fontSize={'10px'} fontWeight={isCurrentLanguage ? '700' : '500'} color={color}>
                {switcherId !== 'mobile' ? props.language.text : props.language.code.toUpperCase()}
            </SText>
        </Button>
    )
}

interface MobileLanguageSwitcherDropDownProps {
    languages: any[]
    isCurrentLanguage: boolean
    onClick: (language: string) => void
    isAuthed: boolean
    switcherId: string
}

const MobileLanguageSwitcherDropDown = (props: MobileLanguageSwitcherDropDownProps) => {
    const { languages, onClick: onClickProp } = props
    const [language, setLanguage] = React.useState('')
    const theme = useSTheme()

    const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setLanguage(event.target.value)
        onClickProp(event.target.value)
    }

    useEffect(() => {
        setLanguage(s18n.language)
    }, [s18n.language])

    return (
        <SBox backgroundColor={theme.colors.secondaryColor} paddingLeft={'5px'}>
            <Icon
                style={{ verticalAlign: 'middle', color: 'white', marginTop: '4px', marginRight: '6px' }}
                fontSize={'small'}
            >
                {'language'}
            </Icon>

            <FormControl variant={'standard'}>
                <NativeSelect
                    defaultValue={languages[0]}
                    value={language}
                    onChange={handleChange}
                    style={{
                        fill: 'white',
                        color: 'white',
                        textDecoration: 'none',
                        fontWeight: 700,
                        fontSize: '16px',
                        minWidth: '30px',
                        maxHeight: '28px',
                        paddingTop: '3px',
                    }}
                    disableUnderline={true}
                >
                    {languages.map((language, index) => (
                        <option key={index} value={language.code}>
                            {language.text.toUpperCase()}
                        </option>
                    ))}
                </NativeSelect>
            </FormControl>
        </SBox>
    )
}

export default LanguageSwitcher
